<script setup>
import { Head } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import InquireForm from '@/Components/Public/InquireForm.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'

defineOptions({ layout: PublicLayout })

const props = defineProps({
  vehicle:   { type: Object, required: true },
  base_path: { type: String, default: '/rentals' }
})
const { t } = useI18n()

const photos = ref(props.vehicle.photo_urls?.length ? props.vehicle.photo_urls : [])
const activeIndex = ref(0)
const eyebrow = computed(() => `${t(`public.classes.${props.vehicle.vehicle_class}`)} · ${t(`public.categories.${props.vehicle.category}`)}`)
const title = computed(() => `${props.vehicle.make} ${props.vehicle.model}${props.vehicle.year ? ` (${props.vehicle.year})` : ''}`)
</script>

<template>
  <Head>
    <title>{{ vehicle.make }} {{ vehicle.model }} — {{ t('public.catalog.head_title') }}</title>
  </Head>

  <BrandHero kind="vehicle" :eyebrow="eyebrow" :title="title">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.catalog.back') }}</a>
    </template>
    <span v-if="vehicle.verified" class="bhero-verified">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>{{ t('public.verified') }}
    </span>
    <p class="bhero-price"><span class="fw-value">{{ vehicle.daily_rate }}</span> <span class="bhero-per">{{ t('public.catalog.per_day') }}</span></p>
    <p v-if="vehicle.deposit" class="bhero-deposit">{{ t('public.catalog.deposit') }}: <span class="fw-value">{{ vehicle.deposit }}</span></p>
    <div class="bhero-ctas">
      <a
        v-for="(url, platform, idx) in vehicle.contact_intents"
        :key="platform"
        :class="['bhero-cta', idx === 0 ? 'bhero-cta--solid' : 'bhero-cta--ghost']"
        :href="url"
        target="_blank"
        rel="noopener"
      >{{ t('public.write_on', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }) }}</a>
      <a class="bhero-cta bhero-cta--ghost" href="#inquire">{{ t('public.catalog.ctas.form') }}</a>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <div class="layout">
        <div class="gallery">
          <div class="gallery__main">
            <img v-if="photos[activeIndex]" :src="photos[activeIndex]" :alt="`${vehicle.make} ${vehicle.model}`">
            <CatalogPlaceholder v-else kind="vehicle" :category="vehicle.category" />
          </div>
          <ul v-if="photos.length > 1" class="gallery__thumbs">
            <li v-for="(p, i) in photos" :key="p">
              <button :class="['thumb', i === activeIndex && 'is-active']" @click="activeIndex = i">
                <img :src="p" alt="">
              </button>
            </li>
          </ul>
        </div>

        <div class="info">
          <h2 class="info__heading">{{ t('public.catalog.specs.title') }}</h2>
          <dl class="specs">
            <template v-if="vehicle.engine_cc"><dt>{{ t('public.catalog.specs.engine') }}</dt><dd>{{ vehicle.engine_cc }}cc</dd></template>
            <template v-if="vehicle.colour"><dt>{{ t('public.catalog.specs.colour') }}</dt><dd>{{ vehicle.colour }}</dd></template>
            <template v-if="vehicle.transmission"><dt>{{ t('public.catalog.specs.transmission') }}</dt><dd>{{ t(`public.transmissions.${vehicle.transmission}`) }}</dd></template>
            <template v-if="vehicle.fuel_type"><dt>{{ t('public.catalog.specs.fuel') }}</dt><dd>{{ t(`public.fuels.${vehicle.fuel_type}`) }}</dd></template>
            <template v-if="vehicle.seats"><dt>{{ t('public.catalog.specs.seats') }}</dt><dd>{{ vehicle.seats }}</dd></template>
          </dl>
        </div>
      </div>

      <div id="inquire" class="inquire">
        <h2 class="inquire__title">{{ t('public.inquire.title') }}</h2>
        <p class="inquire__lede">{{ t('public.inquire.lede') }}</p>
        <InquireForm :topic="'rental'" :vehicle-id="vehicle.id" :prefill-message="`${t('public.inquire.prefill_vehicle')} ${vehicle.make} ${vehicle.model}`" />
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Band content (slotted into BrandHero → inherits the band's light text). */
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-verified {
  display: inline-flex; align-items: center; gap: 0.35rem; margin: 1rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 16%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 35%, transparent);
}
.bhero-verified svg { width: 1rem; height: 1rem; }
.bhero-price { margin: 1.1rem 0 0; font-size: clamp(1.6rem, 3vw, 2.1rem); }
.bhero-per { font-size: 1rem; opacity: 0.85; }
.bhero-deposit { margin: 0.3rem 0 0; font-size: 1rem; opacity: 0.9; }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }
.bhero-cta {
  display: inline-flex; align-items: center; padding: 0.8rem 1.4rem; border-radius: 999px;
  font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1rem; text-decoration: none;
}
.bhero-cta--solid { background: var(--ink); color: var(--accentInk); }
.bhero-cta--ghost { background: color-mix(in oklch, var(--accentInk) 14%, transparent); color: inherit; border: 1px solid color-mix(in oklch, var(--accentInk) 45%, transparent); }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
@media (min-width: 880px) { .layout { grid-template-columns: 1.4fr 1fr; gap: 2.5rem; } }

.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 1rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.gallery__thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 5rem; height: 4rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }

.info__heading { font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1.2rem; color: var(--ink); margin: 0 0 0.75rem; }
.specs { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem 1rem; margin: 0; font-size: 1rem; }
.specs dt { color: var(--muted); margin: 0; }
.specs dd { margin: 0; color: var(--ink); }

.inquire { margin-top: 3rem; padding-top: 2rem; border-top: 1px solid var(--line); max-width: 42rem; }
.inquire__title { font-family: var(--font-display, var(--display)); font-size: 1.6rem; margin: 0 0 0.25rem; color: var(--ink); font-weight: 800; }
.inquire__lede  { color: var(--inkSoft); margin: 0 0 1.5rem; }
</style>
